/**
 * SDK Configuration
 *
 * Feature flag to switch between Agora and VideoSDK.
 * The USE_AGORA setting is fetched from the backend's .env file.
 */

// Import from Agora (now uses agora-rtc-react SDK v2.5.1)
import {
  AgoraConstants,
  AgoraMeetingProvider,
  useAgoraMeeting,
  useAgoraParticipant,
  useAgoraPubSub,
  useAgoraTranscription,
  useAgoraTranslation,
  useAgoraMediaDevice,
  useAgoraWhiteboard,
  createAgoraCameraVideoTrack,
  createAgoraMicrophoneAudioTrack,
  getAgoraNetworkStats,
  AgoraVideoPlayer,
  // New agora-rtc-react SDK components
  LocalUser,
  RemoteUser,
  AgoraRTCProvider,
} from "../agora";

// VideoSDK stubs — USE_AGORA is always true, VideoSDK import disabled
const VideoSDKConstants = {};
const VideoSDKMeetingProvider = ({ children }) => children;

// SDK Configuration state - fetched from backend
let _useAgora = true; // Default to Agora
let _configLoaded = false;
let _configPromise = null;

/**
 * Fetch SDK configuration from backend
 * This should be called early in app initialization
 */
export const fetchSdkConfig = async () => {
  if (_configLoaded) return { useAgora: _useAgora };

  if (_configPromise) return _configPromise;

  _configPromise = (async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BASE_URL}/agora/config`);
      const data = await response.json();
      _useAgora = data.useAgora === true;
      _configLoaded = true;
      console.log('[sdkConfig] Loaded from backend: USE_AGORA =', _useAgora);
      return { useAgora: _useAgora };
    } catch (error) {
      console.error('[sdkConfig] Failed to fetch config from backend, using default (Agora):', error);
      _configLoaded = true;
      return { useAgora: _useAgora };
    }
  })();

  return _configPromise;
};

/**
 * Get current USE_AGORA value
 * Returns the cached value (call fetchSdkConfig first to ensure it's loaded)
 */
export const getUseAgora = () => _useAgora;

/**
 * Check if config has been loaded from backend
 */
export const isConfigLoaded = () => _configLoaded;

// For backwards compatibility - exports the current value
// Note: This is evaluated at module load time, so it uses the default (true)
// For dynamic behavior, use getUseAgora() after calling fetchSdkConfig()
export const USE_AGORA = true; // Default, components should use getUseAgora() for runtime value

// Export the appropriate SDK based on feature flag
// These default to Agora - the actual SDK selection happens in the providers
export const Constants = AgoraConstants;
export const MeetingProvider = AgoraMeetingProvider;
export const useMeeting = useAgoraMeeting;
export const useParticipant = useAgoraParticipant;

// PubSub: Use Agora's implementation
export const usePubSub = useAgoraPubSub;

// Export VideoSDK MeetingProvider and Constants (for reference if needed)
export { VideoSDKMeetingProvider, VideoSDKConstants };
export const useTranscription = useAgoraTranscription;
export const useTranslation = useAgoraTranslation;
export const useMediaDevice = useAgoraMediaDevice;
export const useWhiteboard = useAgoraWhiteboard;
export const createCameraVideoTrack = createAgoraCameraVideoTrack;
export const createMicrophoneAudioTrack = createAgoraMicrophoneAudioTrack;
export const getNetworkStats = getAgoraNetworkStats;
export const VideoPlayer = AgoraVideoPlayer;

// Export agora-rtc-react SDK components (only available when USE_AGORA is true)
export { LocalUser, RemoteUser, AgoraRTCProvider };

// Export both SDK providers for dynamic selection
export {
  AgoraMeetingProvider,
  AgoraConstants,
  useAgoraMeeting,
  useAgoraParticipant,
  useAgoraPubSub,
  useAgoraTranscription,
  useAgoraTranslation,
  useAgoraMediaDevice,
  useAgoraWhiteboard,
  createAgoraCameraVideoTrack,
  createAgoraMicrophoneAudioTrack,
  getAgoraNetworkStats,
  AgoraVideoPlayer,
};

// VideoSDK stubs (never used — USE_AGORA is always true)
const noop = () => ({});
const useVideoSDKMeeting = noop;
const useVideoSDKParticipant = noop;
const useVideoSDKPubSub = noop;
const useVideoSDKTranscription = noop;
const useVideoSDKTranslation = noop;
const useVideoSDKMediaDevice = noop;
const useVideoSDKWhiteboard = noop;
const createVideoSDKCameraVideoTrack = noop;
const createVideoSDKMicrophoneAudioTrack = noop;
const getVideoSDKNetworkStats = noop;
const VideoSDKVideoPlayer = noop;

export {
  useVideoSDKMeeting,
  useVideoSDKParticipant,
  useVideoSDKPubSub,
  useVideoSDKTranscription,
  useVideoSDKTranslation,
  useVideoSDKMediaDevice,
  useVideoSDKWhiteboard,
  createVideoSDKCameraVideoTrack,
  createVideoSDKMicrophoneAudioTrack,
  getVideoSDKNetworkStats,
  VideoSDKVideoPlayer,
};
